import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Car, 
  Camera, 
  MagnifyingGlass, 
  ListChecks, 
  ShieldCheck,
  ArrowRight,
  Clock,
  CurrencyEur,
  Recycle
} from '@phosphor-icons/react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const HomePage = () => {
  const features = [
    {
      icon: Camera,
      title: 'Upload Photos',
      description: 'Téléchargez jusqu\'à 6 photos de votre véhicule accidenté'
    },
    {
      icon: MagnifyingGlass,
      title: 'Analyse Visuelle',
      description: 'Notre logiciel analyse les dommages visibles sur vos images'
    },
    {
      icon: ListChecks,
      title: 'Liste des Pièces',
      description: 'Recevez une liste des pièces potentiellement endommagées'
    },
    {
      icon: ShieldCheck,
      title: 'Devis Gratuit',
      description: 'Demandez un devis personnalisé pour vos pièces'
    }
  ];

  const advantages = [
    {
      icon: Clock,
      title: 'Rapide',
      description: 'Analyse en quelques secondes'
    },
    {
      icon: CurrencyEur,
      title: 'Économique',
      description: 'Pièces d\'occasion garanties'
    },
    {
      icon: Recycle,
      title: 'Écologique',
      description: 'Réemploi et économie circulaire'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Header />
      
      {/* Hero Section - keeps dark overlay for visual impact */}
      <section 
        className="relative min-h-screen flex items-center justify-center pt-20"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1727893119356-1702fe921cf9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxjYXIlMjByZXBhaXIlMjBzaG9wfGVufDB8fHx8MTc3NDYyNzEzMXww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.7) 100%)' }}></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-2 bg-[#E63946]/20 border border-[#E63946]/50 text-[#E63946] text-xs uppercase tracking-[0.2em] font-bold mb-6">
              Nouveau Service
            </span>
            
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tighter text-white mb-6 leading-[0.9]">
              Identifiez vos pièces<br />
              <span className="text-[#E63946]">endommagées</span> en<br />
              quelques clics
            </h1>
            
            <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
              Notre logiciel d'analyse visuelle étudie les photos de votre véhicule accidenté 
              et vous propose une liste de pièces potentiellement endommagées.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/analyse"
                className="bg-[#E63946] text-white px-10 py-5 text-sm font-bold uppercase tracking-wide hover:bg-[#D90429] transition-colors inline-flex items-center justify-center gap-3"
                data-testid="hero-analyze-btn"
              >
                <Camera size={20} weight="bold" />
                Analyser mon véhicule
                <ArrowRight size={20} weight="bold" />
              </Link>
              
              <a
                href="https://ecocasse.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-transparent border border-white/30 text-white px-10 py-5 text-sm font-bold uppercase tracking-wide hover:border-white transition-colors inline-flex items-center justify-center gap-3"
              >
                Voir notre catalogue
              </a>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-white rounded-full"
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#E63946] text-xs uppercase tracking-[0.3em] font-bold">
              Processus
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-gray-900 mt-4">
              Comment ça fonctionne ?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-gray-200 p-8 card-hover shadow-sm"
                >
                  <div className="w-14 h-14 bg-[#E63946]/10 border border-[#E63946]/30 flex items-center justify-center mb-6">
                    <Icon size={28} className="text-[#E63946]" />
                  </div>
                  <span className="text-[#E63946] text-xs uppercase tracking-[0.2em] font-bold">
                    Étape {index + 1}
                  </span>
                  <h3 className="font-['Outfit'] text-xl font-bold text-gray-900 mt-2 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-24 px-6 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#E63946] text-xs uppercase tracking-[0.3em] font-bold">
                Pourquoi Ecocasse
              </span>
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-6">
                Plus de 15 000 000 pi&egrave;ces du r&eacute;seau Ecocasse
              </h2>
              <p className="text-gray-500 leading-relaxed mb-8">
                Ecocasse est votre partenaire de confiance pour les pièces automobiles d'occasion. 
                Notre logiciel d'analyse vous aide à identifier rapidement les pièces dont vous avez besoin, 
                tout en bénéficiant de notre garantie jusqu'à 2 ans.
              </p>
              
              <div className="space-y-4">
                {advantages.map((adv, index) => {
                  const Icon = adv.icon;
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#2A9D8F]/10 border border-[#2A9D8F]/30 flex items-center justify-center flex-shrink-0">
                        <Icon size={20} className="text-[#2A9D8F]" />
                      </div>
                      <div>
                        <h4 className="font-['Outfit'] font-bold text-gray-900">{adv.title}</h4>
                        <p className="text-gray-500 text-sm">{adv.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/4480505/pexels-photo-4480505.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Réparation automobile"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 border border-gray-200"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Outfit'] text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Prêt à analyser votre véhicule ?
            </h2>
            <p className="text-gray-500 text-lg mb-10 max-w-2xl mx-auto">
              Téléchargez vos photos et recevez instantanément une estimation 
              des pièces potentiellement endommagées.
            </p>
            <Link
              to="/analyse"
              className="bg-[#E63946] text-white px-12 py-5 text-sm font-bold uppercase tracking-wide hover:bg-[#D90429] transition-colors inline-flex items-center justify-center gap-3"
              data-testid="cta-analyze-btn"
            >
              <Car size={20} weight="bold" />
              Commencer l'analyse
              <ArrowRight size={20} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-8 px-6 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-400 text-xs leading-relaxed">
            <strong>Avertissement :</strong> L'analyse fournie par notre outil est purement indicative 
            et ne constitue pas un diagnostic professionnel. Les résultats doivent être validés 
            par un expert automobile qualifié avant toute décision d'achat ou de réparation.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
